import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Observable, catchError, from, map, of, switchMap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ImageCacheService {
  private readonly http = inject(HttpClient);
  private readonly sanitizer = inject(DomSanitizer);

  private readonly cacheName = 'ds-msal-image-cache-v1';
  private readonly memoryCache = new Map<string, SafeUrl>();
  private readonly blobUrls = new Map<string, string>();
  private readonly placeholder = this.sanitizer.bypassSecurityTrustUrl('assets/images/placeholder-learner.webp');
  private readonly cacheStorageAvailable = typeof globalThis !== 'undefined' && 'caches' in globalThis;

  get(imageUrl: string, useAvatarPlaceholder?: boolean): Observable<SafeUrl | undefined> {
    if (!imageUrl) {
      return of(useAvatarPlaceholder ? undefined : this.placeholder);
    }

    const inMemory = this.memoryCache.get(imageUrl);
    if (inMemory) {
      return of(inMemory);
    }

    return from(this.getFromCacheStorage(imageUrl)).pipe(
      switchMap((cached) => {
        if (cached) {
          this.memoryCache.set(imageUrl, cached);
          return of(cached);
        }
        return this.fetchAndCache(imageUrl, false, useAvatarPlaceholder);
      }),
      catchError(() => of(useAvatarPlaceholder ? undefined : this.placeholder)),
    );
  }

  // NEW: Clear and refresh a specific image
  clearAndRefresh(imageUrl: string): Observable<SafeUrl | undefined> {
    // Clear from memory cache
    this.clear(imageUrl);

    // Force fetch fresh image (bypass cache)
    return this.fetchAndCache(imageUrl, true);
  }

  preloadImage(imageUrl: string): Observable<SafeUrl | undefined> {
    return this.fetchAndCache(imageUrl);
  }

  clearAll(): void {
    this.blobUrls.forEach((url) => URL.revokeObjectURL(url));
    this.blobUrls.clear();
    this.memoryCache.clear();

    if (this.cacheStorageAvailable) {
      caches.delete(this.cacheName).catch(() => undefined);
    }
  }

  // Force refresh specific image with cache busting
  forceRefresh(imageUrl: string): Observable<SafeUrl | undefined> {
    // Add timestamp to bust cache
    const cacheBustedUrl = this.addCacheBuster(imageUrl);

    // Clear old cache
    this.clear(imageUrl);

    // Fetch with cache-busted URL
    return this.fetchAndCache(cacheBustedUrl, true, false).pipe(
      switchMap((safeUrl) => {
        // Store under original URL for future requests
        if (safeUrl) {
          this.memoryCache.set(imageUrl, safeUrl);
        }
        return of(safeUrl);
      }),
    );
  }

  clear(imageUrl?: string): void {
    if (!imageUrl) {
      this.clearAll();
      return;
    }

    const blobUrl = this.blobUrls.get(imageUrl);
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      this.blobUrls.delete(imageUrl);
    }
    this.memoryCache.delete(imageUrl);

    if (this.cacheStorageAvailable) {
      caches
        .open(this.cacheName)
        .then((cache) => cache.delete(imageUrl))
        .catch(() => undefined);
    }
  }

  private fetchAndCache(
    imageUrl: string,
    bypassCache = false,
    useAvatarPlaceholder = false,
  ): Observable<SafeUrl | undefined> {
    const headers: Record<string, string> = {};
    if (bypassCache) {
      headers['Cache-Control'] = 'no-cache';
      headers['Pragma'] = 'no-cache';
    }

    return this.http.get(imageUrl, { responseType: 'blob', headers }).pipe(
      switchMap((blob) => from(this.storeInCache(imageUrl, blob)).pipe(map(() => this.createSafeUrl(imageUrl, blob)))),
      catchError((error) => {
        console.error('Failed to fetch image:', imageUrl, error);
        return of(useAvatarPlaceholder ? undefined : this.placeholder);
      }),
    );
  }

  private async getFromCacheStorage(imageUrl: string): Promise<SafeUrl | null> {
    if (!this.cacheStorageAvailable) {
      return null;
    }

    try {
      const cache = await caches.open(this.cacheName);
      const match = await cache.match(imageUrl);
      if (!match) {
        return null;
      }

      const blob = await match.blob();
      return this.createSafeUrl(imageUrl, blob);
    } catch {
      return null;
    }
  }

  private async storeInCache(imageUrl: string, blob: Blob): Promise<void> {
    if (!this.cacheStorageAvailable) {
      return;
    }

    try {
      const cache = await caches.open(this.cacheName);
      const response = new Response(blob, {
        headers: {
          'Content-Type': blob.type || 'application/octet-stream',
        },
      });

      await cache.put(imageUrl, response);
    } catch {
      return;
    }
  }

  private createSafeUrl(imageUrl: string, blob: Blob): SafeUrl {
    const objectUrl = URL.createObjectURL(blob);
    const safeUrl = this.sanitizer.bypassSecurityTrustUrl(objectUrl);
    this.memoryCache.set(imageUrl, safeUrl);
    this.blobUrls.set(imageUrl, objectUrl);
    return safeUrl;
  }

  // Helper method to add cache buster
  private addCacheBuster(url: string): string {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}_t=${Date.now()}`;
  }
}
